<template>
  <!-- Use of 'unset' on width should ensure dialog is resized to contents: https://stackoverflow.com/questions/55085735/vuetify-v-dialog-dynamic-width -->
  <v-dialog
    v-if="visible ?? true"
    :value="true"
    :width="width"
    :persistent="!closeable || !isDesktopView"
    scrollable
    :fullscreen="!isDesktopView"
    data-qa="bm_dialog"
    :style="{ 'z-index': 500 }"
    @input="onVDialogInput"
  >
    <bm-card :label="label" :class="dialogType.dialogClass">
      <bm-form ref="form" :value.sync="valid" prevent-submit>
        <slot name="default" :valid="valid" />
      </bm-form>

      <template v-if="closeable" #menu>
        <bm-btn :x-large="large" icon button-type="ghost" @click="cancel">
          <bm-icon icon="HighlightOff" data-qa="dialog_menu_close" />
        </bm-btn>
      </template>

      <template #controls>
        <slot
          name="controls"
          :valid="valid"
          :dirty="dirty"
          :accept="validateAndAccept"
          :cancel="cancel"
          :cancelable="cancelable"
          :dialog-type="dialogType"
        >
          <bm-dialog-controls
            :valid="valid"
            :dirty="dirty"
            :accept="validateAndAccept"
            :cancel="cancel"
            :cancelable="cancelable"
            :dialog-type="dialogType"
            :large="large"
          >
            <template #controls-confirm-text="scope">
              <slot name="controls-confirm-text" v-bind="scope" />
            </template>
            <template #controls-cancel-text="scope">
              <slot name="controls-cancel-text" v-bind="scope" />
            </template>
            <template #controls-confirm="scope">
              <slot name="controls-confirm" v-bind="scope" />
            </template>
            <template #controls-cancel="scope">
              <slot name="controls-cancel" v-bind="scope" />
            </template>
          </bm-dialog-controls>
        </slot>
      </template>
    </bm-card>
  </v-dialog>
</template>

<script setup lang="ts">
import BmBtn from "@/components/controls/input/BmBtn.vue";
import BmCard from "@/components/controls/card/BmCard.vue";
import BmDialogControls from "@/components/dialogs/BmDialogControls.vue";
import type { DialogEvents } from "@/components/mixins/dialogs";
import { useReactiveDialogScriptSetup } from "@/components/mixins/dialogs";
import { computed } from "vue";
import { useFormValidator } from "@/components/mixins/validatable";
import { EnumDialogType } from "@/components/dialogs/DialogTypes";
import { screenSize } from "@/vue23switching/screenSize";
import type { Maybe } from "../utils/typeHelpers";

// Defines key exported functions to be called externally
export type BmDialogInterface = {
  reset(): void;
  resetValidation(): void;
  validate(): boolean;
  isValid(): boolean;
};

/**
 * Generic BmDialog component
 *
 * Width = Pixel width or 'unset' to dynamically resize based on contents.
 *
 * @default: Primary 'OK' button, Secondary 'Cancel' button
 * @slots
 * 'controls', to completely replace the controls section
 *
 * @events
 * 'cancel', normally used to close the menu
 * 'accept', validates the form then confirm/submit the contents of the dialog.
 */

interface Props {
  label: string;
  visible?: Maybe<boolean>;
  cancelable?: boolean;
  closeable?: boolean;
  width?: number | string;
  dirty?: boolean;
  dialogType?: EnumDialogType;
  large?: boolean;
}

interface Events extends DialogEvents {}

withDefaults(defineProps<Props>(), {
  label: "",
  cancelable: true,
  closeable: true,
  width: 600,
  dirty: true, // Default true for dialogs non-reliant on dirty state
  dialogType: () => EnumDialogType.info,
  large: false,
});

const emit = defineEmits<Events>();

const { valid, validate, form, reset, resetValidation } = useFormValidator();
const { accept, cancel } = useReactiveDialogScriptSetup(emit);

const isDesktopView = computed(() => screenSize.smAndUp);

/**
 * This may be called by the Vuetify dialog if the user clicks outside the dialog - or the escape key is pressed.
 */
const onVDialogInput = (val: boolean) => {
  if (!val) {
    cancel();
  }
};

/**
 * Perform form validation before accepting.
 */
const validateAndAccept = (payload?: any) => {
  if (validate()) {
    accept(payload);
  }
};

defineExpose<BmDialogInterface>({
  reset,
  resetValidation,
  validate,
  isValid: () => valid.value,
});
</script>

<style scoped lang="sass">
@import "src/sass/typography"
@import "src/sass/colours"

:deep(.bm-card-error .bm-card-title), :deep(.bm-card-error .bm-card-title .v-icon)
  color: $bm-color-text04
  background-color: $bm-color-error-medium !important

:deep(.bm-card-warning .bm-card-title), :deep(.bm-card-warning .bm-card-title .v-icon)
  background-color: $bm-color-warning-medium !important

:deep(.bm-card-success .bm-card-title), :deep(.bm-card-success .bm-card-title .v-icon)
  color: $bm-color-text04
  background-color: $bm-color-success-medium !important

:deep(.bm-card-info .bm-card-title), :deep(.bm-card-info .bm-card-title .v-icon)
  color: $bm-color-text04
  background-color: $bm-color-info-medium !important
</style>
